
import React, { useEffect } from 'react';
import { useCookieConsent } from '@/context/CookieConsentContext';
import Logger from '@/utils/logger';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GOOGLE_ANALYTICS_ID = 'G-3CL1HR0DLJ';

const AnalyticsWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { preferences, consentGiven } = useCookieConsent();

  useEffect(() => {
    // Only initialize analytics if consent is given and analytics cookies are accepted
    if (consentGiven && preferences.analytics) {
      initializeGoogleAnalytics();
    }
  }, [consentGiven, preferences.analytics]);

  const initializeGoogleAnalytics = () => {
    try {
      // If script already exists, do not re-inject
      if (document.querySelector(`script[src*="id=${GOOGLE_ANALYTICS_ID}"]`)) {
        return;
      }

      // Create script tag for gtag
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
      
      // IMPORTANT: Use the onload callback to run commands after the script is loaded
      script.onload = () => {
        // Initialize dataLayer and gtag function
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag(...args: any[]) {
          window.dataLayer.push(args);
        };

        // Set initial 'js' command
        window.gtag('js', new Date());

        // Configure Google Analytics
        window.gtag('config', GOOGLE_ANALYTICS_ID, {
          anonymize_ip: true,
          cookie_flags: 'SameSite=Strict;Secure',
          send_page_view: true, // Let GA handle the initial page view automatically upon config
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        });

        Logger.info('Google Analytics initialized successfully.');
      };

      // Handle script loading errors
      script.onerror = () => {
        Logger.error('Failed to load Google Analytics script.');
      };

      // Append the script to the head to start loading
      document.head.appendChild(script);

    } catch (error) {
      Logger.error('Failed to initialize Google Analytics:', error);
    }
  };

  return <>{children}</>;
};

// Utility functions for tracking
export const trackPageView = (path: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Send page_view event for SPA route changes
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

export default AnalyticsWrapper;
